import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { API_BASE, SHEETS } from "../utils/api";
import { buildCartFirmsCachePayload } from "./Login";
import {
  digits10,
  getAdminSession,
  getViewAs,
  restoreAdminSession,
  clearAdminSession,
  viewAsDealer,
} from "../utils/adminView";

const cleanGst = (v) =>
  String(v ?? "")
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "");

// Admin (all dealers) / NBD (only their dealers): pick whose dashboard to open.
export default function SelectDealer() {
  const navigate = useNavigate();
  const admin = getAdminSession();
  const current = getViewAs();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const isNbd = admin?.scope === "nbd";
  const [staff, setStaff] = useState(null); // NBD: { staff, gsts }

  useEffect(() => {
    if (!admin) return;
    let cancelled = false;
    const staffReq = isNbd
      ? fetch(
          `${API_BASE}/api/sheets/staff-dealers?phone=${encodeURIComponent(admin.phone)}`,
          {
            cache: "no-store",
          },
        ).then((r) => r.json())
      : Promise.resolve(null);
    Promise.all([
      fetch(SHEETS.dealers(), { cache: "no-store" }).then((r) => r.json()),
      staffReq,
    ])
      .then(([d, s]) => {
        if (cancelled) return;
        setStaff(s);
        setRows(Array.isArray(d) ? d : []);
      })
      .catch(() => {
        if (!cancelled)
          setError("Could not load the dealer list. Please refresh.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One entry per login number (a number can have several firms).
  const dealers = useMemo(() => {
    const byPhone = new Map();
    // NBD → only the firms whose GST is theirs in RETENTION FOR ALL.
    const allowed = isNbd ? new Set((staff?.gsts || []).map(cleanGst)) : null;
    for (const r of rows || []) {
      if (allowed && !allowed.has(cleanGst(r?.["GSTIN/UIN"]))) continue;
      const phone = digits10(r?.["Contact No"]);
      if (phone.length !== 10) continue;
      if (!byPhone.has(phone)) byPhone.set(phone, []);
      byPhone.get(phone).push(r);
    }
    return [...byPhone.entries()].map(([phone, list]) => {
      const r = list[0];
      const firms = [
        ...new Set(
          list.map((x) => String(x.Particulars || "").trim()).filter(Boolean),
        ),
      ];
      const gsts = [
        ...new Set(
          list.map((x) => String(x["GSTIN/UIN"] || "").trim()).filter(Boolean),
        ),
      ];
      return {
        phone,
        rows: list,
        firms,
        gsts,
        person: String(r["Contact Person Name"] || "").trim(),
        district: String(r.District || r.Area || "").trim(),
        type: String(r.Type || "").trim(),
        search: [
          phone,
          ...firms,
          ...gsts,
          r["Contact Person Name"],
          r.District,
          r.Area,
          r.Type,
          r.CRE,
        ]
          .join(" ")
          .toLowerCase(),
      };
    });
  }, [rows, staff, isNbd]);

  const shown = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const list = words.length
      ? dealers.filter((d) => words.every((w) => d.search.includes(w)))
      : dealers;
    return list.slice(0, 100);
  }, [dealers, query]);

  if (!admin) return <Navigate to="/login-success" replace />;

  const open = (d) => {
    viewAsDealer(
      d.phone,
      d.rows,
      buildCartFirmsCachePayload(d.rows, d.phone, d.rows[0]),
    );
    // Full reload so every page / cache starts fresh as this dealer.
    window.location.assign("/dashboard");
  };

  const openOwn = () => {
    restoreAdminSession();
    window.location.assign("/dashboard");
  };

  const logout = () => {
    clearAdminSession();
    localStorage.removeItem("dealer");
    localStorage.removeItem("role");
    localStorage.removeItem("loginAt");
    localStorage.removeItem("loginPhone");
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-yellow-400 to-amber-500 px-4 py-4 shadow">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">
              {isNbd ? "NBD" : "Admin"} — Select Dealer
            </h1>
            <p className="text-xs font-semibold text-gray-800">
              Logged in as {isNbd ? staff?.staff?.name || "NBD" : "admin"}{" "}
              {admin.phone}
              {isNbd && staff?.staff?.email ? ` (${staff.staff.email})` : ""}.
              Pick a dealer to view the portal as that dealer.
            </p>
          </div>
          <div className="flex gap-2">
            {!isNbd && (
              <button
                type="button"
                onClick={openOwn}
                className="text-sm font-bold bg-white/90 hover:bg-white text-gray-900 px-3 py-1.5 rounded-full"
              >
                My own dashboard
              </button>
            )}
            <button
              type="button"
              onClick={logout}
              className="text-sm font-bold bg-gray-900 hover:bg-black text-white px-3 py-1.5 rounded-full"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5">
        {current?.phone && (
          <p className="mb-3 text-sm text-gray-600">
            Currently viewing: <b>{current.firmName || current.phone}</b>{" "}
            ({current.phone})
          </p>
        )}
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search firm name, phone, GSTIN, person, district…"
          className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <p className="mt-2 text-xs text-gray-500">
          {rows == null
            ? "Loading dealers…"
            : `${dealers.length.toLocaleString("en-IN")} dealers · showing ${shown.length}${
                shown.length === 100 ? " (type to narrow down)" : ""
              }`}
        </p>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        {isNbd && rows != null && dealers.length === 0 && !error && (
          <p className="mt-3 text-sm text-amber-700">
            {staff?.found === false
              ? "Your number is not set as NBD in the INFO sheet."
              : "No dealers found with your email in the NBD EMAIL column of RETENTION FOR ALL."}
          </p>
        )}

        <div className="mt-3 grid gap-2">
          {shown.map((d) => (
            <button
              key={d.phone}
              type="button"
              onClick={() => open(d)}
              className={`text-left bg-white border rounded-xl px-4 py-3 shadow-sm hover:border-amber-400 hover:bg-amber-50 transition ${
                current?.phone === d.phone
                  ? "border-amber-400"
                  : "border-gray-200"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 truncate">
                    {d.firms[0] || "—"}
                  </p>
                  {d.firms.length > 1 && (
                    <p className="text-xs text-gray-500 truncate">
                      + {d.firms.slice(1).join(", ")}
                    </p>
                  )}
                  <p className="text-xs text-gray-600 mt-0.5">
                    {[d.person, d.district, d.gsts.join(", ")]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-sm font-semibold text-gray-800">
                    {d.phone}
                  </p>
                  {d.type && (
                    <span className="inline-block mt-1 text-[10px] font-bold uppercase bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      {d.type}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
